import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IdentityStorage } from 'storage-service-lib';
import { ServiceProviderServiceFactory } from '../../../../services/service-providers/service-provider-service.factory';
import { ActionTypes, TargetTypes, UpdateFieldAction } from './update-field.service';

@Component({
    selector: 'app-update-field',
    templateUrl: './update-field.component.html',
    styleUrls: ['./update-field.component.scss'],
    standalone: false
})
export class UpdateFieldComponent implements OnInit, OnDestroy {
  static NAME = 'update-field';

  private static readonly DOCUSIGN_FIELD_PATTERN = /^[a-zA-Z0-9_]+$/;

  static IUpdateFieldConfig: {
    showDateSentOption: boolean;
    showDateSavedOption: boolean;
    showDocusignOption: boolean;
  };

  private get getActionControlValue(): UpdateFieldAction {
    return this.actionControl.value;
  }

  @Input() actionControl: FormGroup;
  @Input() updateFieldConfig: any;
  @Output() delete: EventEmitter<void> = new EventEmitter();

  viewFormGroup: FormGroup = new FormGroup({
    fieldName: new FormControl(null, Validators.required),
    actionType: new FormControl(null, Validators.required),
    targetType: new FormControl(null, Validators.required),
    viewValue: new FormControl(null, this.viewValueValidation()),
  });

  actionTypeOptions: { type: string; label: string; targetType: TargetTypes; expression: string }[] = [];
  targetTypeOptions = Object.keys(TargetTypes);
  hideViewValueOption = false;
  viewFormGroupValueChanges: Subscription = undefined;

  constructor(private serviceProviderServiceFactory: ServiceProviderServiceFactory, private identityStorage: IdentityStorage) {}

  ngOnInit() {
    this.buildActionTypeOptions();
    this.setupFormGroup();

    this.actionControl.setControl('viewFormGroup', this.viewFormGroup);
    this.viewFormGroupValueChanges = this.viewFormGroup.valueChanges.subscribe((_) => {
      this.updateActionControl();
    });
  }

  removeAction() {
    this.delete.emit();
  }

  updateActionType(selectedActionType: any): void {
    const option = this.actionTypeOptions.find((value) => value.type === selectedActionType.value);
    this.hideViewValueOption = this.isDateActionType(option.type);
    this.viewFormGroup.get('viewValue').updateValueAndValidity({ emitEvent: false });
  }

  updateActionControl() {
    const fieldName = this.viewFormGroup.get('fieldName').value;
    const targetType = this.viewFormGroup.get('targetType').value;
    const actionType = this.viewFormGroup.get('actionType').value;
    const viewValue = this.viewFormGroup.get('viewValue').value;

    this.actionControl
      .get('properties')
      .get('fields')
      .setValue({ [fieldName]: this.buildExpression(actionType, targetType, viewValue) });
    this.actionControl.get('properties').get('actionType').setValue(actionType);
    this.actionControl
      .get('properties')
      .get('targetTypes')
      .setValue({ [fieldName]: targetType });

    if (actionType === ActionTypes.docusign) {
      const path = this.getSignatureFormDataService().buildSignatureTabLabelPath(viewValue);
      this.actionControl
        .get('properties')
        .get('sourcePaths')
        .setValue({ [fieldName]: path });
    }

    this.actionControl.markAsDirty();
  }

  private buildExpression(actionType: ActionTypes, targetType, viewValue): string {
    if (this.isDateActionType(actionType)) {
      const dateOption = this.actionTypeOptions.find((value) => value.type === actionType);
      return dateOption.expression;
    } else if (actionType === ActionTypes.docusign) {
      const path = this.getSignatureFormDataService().buildSignatureTabLabelPath(viewValue);
      return this.getFormDataService().formatDataPath(targetType, path);
    } else if (actionType === ActionTypes.fixed) {
      return viewValue;
    }
  }

  private setupFormGroup() {
    const properties = this.getActionControlValue.properties;

    const getFirstFieldName = () => Object.keys(properties.fields)?.[0] ?? '';
    const field = getFirstFieldName(); // currently only one field per action, get the first one
    const actionType: ActionTypes = properties.actionType ?? ActionTypes.fixed;
    const targetType = properties.targetTypes[field] ?? TargetTypes.string;
    const viewValue = this.getViewValue(properties.actionType, properties.sourcePaths[field], properties.fields[field]);

    this.viewFormGroup.get('fieldName').setValue(field);
    this.viewFormGroup.get('actionType').setValue(actionType);
    this.viewFormGroup.get('targetType').setValue(targetType);
    this.viewFormGroup.get('viewValue').setValue(viewValue);

    this.hideViewValueOption = this.isDateActionType(actionType);
  }

  private getViewValue(actionType: ActionTypes, path: string, expression: string) {
    switch (actionType) {
      case ActionTypes.docusign:
        return this.getSignatureFormDataService().getSignatureTabLabel(path);
      case ActionTypes.dateSent:
      case ActionTypes.dateSaved:
        return null;
      case ActionTypes.fixed:
        return expression;
      default:
        return expression;
    }
  }

  private buildActionTypeOptions() {
    this.actionTypeOptions.push({ type: ActionTypes.fixed, label: 'Fixed contents', targetType: TargetTypes.string, expression: '' });

    if (this.updateFieldConfig.showDocusignOption) {
      this.actionTypeOptions.push({ type: ActionTypes.docusign, label: 'Docusign field', targetType: TargetTypes.string, expression: '' });
    }

    if (this.updateFieldConfig.showDateSentOption) {
      this.actionTypeOptions.push({
        type: ActionTypes.dateSent,
        label: 'Date sent',
        targetType: TargetTypes.date,
        expression: '[(${#temporals.format(signature.sentResponse.properties.dateSent, \'MM/dd/yyyy\')})]',
      });
    }

    if (this.updateFieldConfig.showDateSavedOption) {
      this.actionTypeOptions.push({
        type: ActionTypes.dateSaved,
        label: 'Date saved',
        targetType: TargetTypes.date,
        expression: '[(${#temporals.format(#temporals.createNow(), \'MM/dd/yyyy\')})]',
      });
    }
  }

  private isDateActionType(actionType: string): boolean {
    return actionType === ActionTypes.dateSent || actionType === ActionTypes.dateSaved;
  }

  private getSignatureFormDataService() {
    return this.serviceProviderServiceFactory.getSignatureProviderService('docusign').getSignatureFormDataService();
  }

  private getFormDataService() {
    return this.serviceProviderServiceFactory
      .getServiceProviderService(this.identityStorage.get().namespace)
      .getSignatureDataFormatterService();
  }

  private viewValueValidation(): ValidatorFn {
    return (form: FormControl): ValidationErrors | null => {
      const actionType: string = form?.parent?.get('actionType')?.value;
      const viewValue: string = form?.parent?.get('viewValue')?.value;

      const actionTypeRequiresViewValue = actionType === ActionTypes.fixed || actionType === ActionTypes.docusign;
      if (!actionTypeRequiresViewValue) {
        return null;
      }

      const viewValueIsBlank = !viewValue || viewValue.trim() === '';
      if (viewValueIsBlank) {
        return { viewValue: false };
      }

      const invalidDocusignCharacters = !UpdateFieldComponent.DOCUSIGN_FIELD_PATTERN.test(viewValue);
      if (actionType === ActionTypes.docusign && invalidDocusignCharacters) {
        return { viewValue: false };
      }

      return null;
    };
  }

  ngOnDestroy(): void {
    this.viewFormGroupValueChanges?.unsubscribe();
  }
}
